import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useI18n } from "@/lib/i18n";
import { X, Megaphone, Gift, Truck, Clock, Bell, Star } from "lucide-react";

const BASE_URL = import.meta.env.BASE_URL || "/";

interface NotificationBar {
  id: number;
  text: string;
  textAr: string;
  bgColor: string;
  textColor: string;
  icon: string;
  dismissible: boolean;
  enabled: boolean;
  position?: string;
}

export const NOTIFICATION_BARS_QUERY_KEY = ["notification-bars"] as const;

const ICON_MAP: Record<string, React.ReactNode> = {
  megaphone: <Megaphone className="h-3.5 w-3.5 shrink-0" />,
  gift: <Gift className="h-3.5 w-3.5 shrink-0" />,
  truck: <Truck className="h-3.5 w-3.5 shrink-0" />,
  clock: <Clock className="h-3.5 w-3.5 shrink-0" />,
  bell: <Bell className="h-3.5 w-3.5 shrink-0" />,
  star: <Star className="h-3.5 w-3.5 shrink-0" />,
};

export function NotificationBars({ position = "top" }: { position?: "top" | "bottom" } = {}) {
  const { lang } = useI18n();
  const { data: rawBars = [] } = useQuery<NotificationBar[]>({
    queryKey: NOTIFICATION_BARS_QUERY_KEY,
    queryFn: async () => {
      const r = await fetch(`${BASE_URL}api/notification-bars`);
      if (!r.ok) return [];
      const data = await r.json();
      return Array.isArray(data) ? data : [];
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const bars = rawBars.filter((b) => b.enabled);
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());

  useEffect(() => {
    try {
      const stored = localStorage.getItem("dismissed_bars");
      if (stored) setDismissed(new Set(JSON.parse(stored)));
    } catch {}
  }, []);

  const dismissBar = (id: number) => {
    const updated = new Set(dismissed);
    updated.add(id);
    setDismissed(updated);
    localStorage.setItem("dismissed_bars", JSON.stringify([...updated]));
  };

  const visibleBars = bars.filter(b => {
    if (dismissed.has(b.id)) return false;
    const pos = (b.position === "bottom" ? "bottom" : "top");
    return pos === position;
  });

  if (visibleBars.length === 0) return null;

  return (
    <div className="w-full">
      {visibleBars.map((bar) => (
        <div
          key={bar.id}
          className="relative flex items-center justify-center gap-2 py-1.5 px-4 text-center"
          style={{ backgroundColor: bar.bgColor || "#000", color: bar.textColor || "#fff" }}
        >
          {ICON_MAP[bar.icon]}
          <span className="text-[11px] tracking-wider font-medium">
            {lang === "ar" ? bar.textAr : bar.text}
          </span>
          {bar.dismissible && (
            <button
              onClick={() => dismissBar(bar.id)}
              className="absolute end-3 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100 transition-opacity"
              aria-label={lang === "ar" ? "إغلاق الإشعار" : "Dismiss notification"}
            >
              <X className="h-3 w-3" aria-hidden="true" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
